import {
  DiscoveryProductsBestSellerGroupResponseType,
  DiscoveryProductsRecentlyAddedResponseType,
  DiscoveryRecentlyAddedGroupResponseType,
} from "@sellar-io/api-v2/types";
import { UseQueryResult } from "@tanstack/react-query";
import { useShallow } from "zustand/react/shallow";
import { SpinnerLegacy } from "../../../../components";
import { useMarketStorefrontSessionId } from "../../../../hooks/useMarketStorefrontSessionTracking";
import { DiscoveryListProductCard } from "../DiscoveryListProductCard";
import { useDiscoveryV2SearchStore } from "../DiscoveryV2SearchStore";
import { DiscoveryHorizontalList } from "./DiscoveryHorizontalList";
import { useEvents } from "../../../../hooks";
import { useState } from "react";
import {
  setRecentlyViewedExpiry,
  useDiscoveryV2RecentlyViewedStore,
} from "../DiscoveryV2RecentlyViewedStore";
import { getSellarWebsiteUtms } from "../../../../utils/utmHelper";
import { DiscoveryProductDialog } from "./DiscoveryProductDialog";
import { BestPricePromiseDialog } from "../../../../components/bestPricePromise/BestPricePromiseDialog";

export const DiscoverySubPageListsView = ({
  query,
  onRedirect,
}: {
  query: UseQueryResult<
    | DiscoveryRecentlyAddedGroupResponseType
    | DiscoveryProductsBestSellerGroupResponseType
  >;
  onRedirect: () => void;
}) => {
  const { setContainerTypes, addAbvType, addStyleSearchValue } =
    useDiscoveryV2SearchStore(
      useShallow((state) => ({
        setContainerTypes: state.setContainerTypes,
        setSearchValue: state.setSearchValue,
        addAbvType: state.addAbvType,
        addStyleSearchValue: state.addStyleSearchValue,
      })),
    );

  if (query.isPending || query.isLoading) return <SpinnerLegacy />;

  const setFilter = (
    group:
      | DiscoveryRecentlyAddedGroupResponseType[number]
      | DiscoveryProductsBestSellerGroupResponseType[number],
  ) => {
    if (group.searchStyle) {
      return addStyleSearchValue(group.searchStyle);
      // return setSearchValue(group.searchTerm);
    }

    if (!!group.containerTypes.length) {
      return setContainerTypes(group.containerTypes);
    }

    if (group.abv) {
      addAbvType(group.abv);
    }
  };
  return (
    <>
      <div className={"m-auto max-w-300 flex-col gap-8"}>
        {(query.data ?? []).map((group) => (
          <DiscoveryV2RecentlyAddedSection
            key={group.title}
            sectionTitle={group.title}
            products={group.results}
            onClick={() => {
              setFilter(group);
              onRedirect();
            }}
          />
        ))}
      </div>
    </>
  );
};

const DiscoveryV2RecentlyAddedSection = ({
  sectionTitle,
  products,
  onClick,
}: {
  sectionTitle: string;
  products: DiscoveryRecentlyAddedGroupResponseType[number]["results"];
  onClick?: () => void;
}) => {
  const { track } = useEvents();
  const { sessionId } = useMarketStorefrontSessionId();
  const [selectedProduct, setSelectedProduct] = useState<
    DiscoveryProductsRecentlyAddedResponseType[number] | undefined
  >(undefined);
  const [bestPricePromiseDialogActive, setBestPricePromiseDialogActive] =
    useState<boolean>(false);

  const onProductClicked = (
    product: DiscoveryProductsRecentlyAddedResponseType[number],
  ) => {
    if (product.Supplier) {
      setSelectedProduct(product);
      useDiscoveryV2RecentlyViewedStore.getState().add({
        id: product.id,
        supplierId: product.Supplier.id,
        name: product.name ?? "",
        imageUrl: product.imageUrl,
        logoUrl: product.Supplier.Business?.logoUrl,
        urlId: product.Supplier.urlId,
        details: `${product.kind} // ${product.abv ?? ""}%`,
        expiry: setRecentlyViewedExpiry(),
        containsPreOrderProductItem: product.containsPreOrderProductItem,
      });

      track("Discover product clicked", {
        supplier: product.Supplier.urlId,
        product: product.name,
        source: "Recently added",
        containsPreOrderProductItem: product.containsPreOrderProductItem,
        sessionId,
        ...getSellarWebsiteUtms(),
      });
    }
  };

  return (
    <>
      <div className={"mb-4 flex flex-col gap-2"}>
        <DiscoveryHorizontalList
          title={sectionTitle}
          onClick={() => onClick?.()}
        >
          {products.map((product) => (
            <DiscoveryListProductCard
              key={product.id}
              product={product}
              onClick={() => onProductClicked(product)}
            />
          ))}
        </DiscoveryHorizontalList>
      </div>

      {selectedProduct && (
        <>
          <DiscoveryProductDialog
            productId={selectedProduct.id}
            supplierId={selectedProduct.Supplier?.id}
            active={!!selectedProduct}
            onDismiss={() => {
              setSelectedProduct(undefined);
            }}
            setBestPricePromiseDialogActive={() => {
              setBestPricePromiseDialogActive(true);
            }}
          />

          <BestPricePromiseDialog
            productName={selectedProduct.name}
            buyerName={selectedProduct.name}
            supplierName={selectedProduct.Supplier?.Business?.name}
            active={bestPricePromiseDialogActive}
            onDismiss={() => setBestPricePromiseDialogActive(false)}
          />
        </>
      )}
    </>
  );
};
